import React, { useCallback, useEffect, useState } from 'react';
import { useToastStore } from '../stores/ToastStore';
import { useContextMenu } from '../hooks/useContextMenu';
import { ContextMenuPortal } from './common/ContextMenuPortal';
import { IExportService } from '../types/exportTypes';
import { useChatListStore } from '../stores/chatListStore';
import { useChatUIStore } from '../stores/chatUIStore';
import { useChatHistoryStore } from '../stores/chatHistoryStore';

interface ChatHistoryProps {
    /** Platform-specific export service for chat management modal */
    exportService?: IExportService;
    /** Callback to close mobile overlay */
    onCloseMobile?: () => void;
    /** Lifted chat management modal setter */
    setIsChatManagementOpen: (open: boolean) => void;
    /** Lifted export chat id setter */
    setExportChatId: (id: string | null) => void;
}

export function ChatHistory({ onCloseMobile, setIsChatManagementOpen, setExportChatId }: ChatHistoryProps) {
    const selectChat = useChatListStore((s) => s.selectChat);
    const removeChat = useChatListStore((s) => s.removeChat);
    const chats = useChatListStore((s) => s.chats);
    const chatId = useChatHistoryStore((s) => s.chatId);
    const setRenameOpen = useChatUIStore((s) => s.setRenameOpen);
    const setTargetChatId = useChatUIStore((s) => s.setTargetChatId);
    const setTargetChatTitle = useChatUIStore((s) => s.setTargetChatTitle);

    const { setModalOpen } = useToastStore();
    const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

    // Use the custom hook for context menu logic
    const {
        contextMenuOpen,
        toggleContextMenu,
        menuPosition,
        menuRef,
        handleMenuAction,
    } = useContextMenu();

    const handleChatSelection = useCallback((chatIdtoSelect: string) => {
        setSelectedChatId(chatIdtoSelect);
        selectChat(chatIdtoSelect);
    }, [selectChat]);

    // User-initiated chat selection: also closes mobile overlay
    const handleChatClick = useCallback((chatIdtoSelect: string) => {
        handleChatSelection(chatIdtoSelect);
        onCloseMobile?.();
    }, [handleChatSelection, onCloseMobile]);

    useEffect(() => {
        if (chatId) {
            setSelectedChatId(chatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId]);

    return (
        <div className="mb-5 pl-2.5 h-2/5 overflow-hidden mt-10 border-b-2 border-neutral-400">
            <div className="flex items-center justify-between mb-4 mr-4">
                <p className="text-xs ml-4 font-semibold">HISTORY</p>
                <button
                    onClick={() => {
                        setExportChatId(null);
                        setIsChatManagementOpen(true);
                        setModalOpen(true);
                    }}
                    className="bg-transparent border-0 cursor-pointer text-neutral-100 hover:text-red-600 transition-colors duration-200"
                    title="Manage chat history"
                    aria-label="Manage chat history"
                    type="button"
                >
                    <i className="fas fa-history text-sm"/>
                </button>
            </div>
            <div
                className={`h-[calc(100%-40px)] overflow-y-hidden pr-2.5 ${!contextMenuOpen ? 'hover:overflow-y-auto' : 'overflow-y-hidden pr-5'}`}
            >
                {chats.map((item, index) => (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                    <div
                        key={item.id}
                        className={`flex items-center w-full ${selectedChatId === item.id ? 'active' : ''}`}
                        onClick={() => handleChatClick(item.id)}
                        data-testid="chat-history-item"
                        aria-label={item.title}
                    >
                        <div
                            className={`flex items-center w-full overflow-x-hidden transition-colors duration-200 rounded-small m-0.5 p-1.5 pl-2.5 hover:bg-neutral-700 group ${contextMenuOpen === `history-${index}` ? 'bg-neutral-700' : ''} ${selectedChatId === item.id ? 'bg-neutral-700' : ''}`}
                        >
                            <button
                                className="flex-grow w-full bg-transparent border-0 text-left whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 text-neutral-200 text-sm group-hover:ml-1"
                                aria-label={item.title}
                                type="button"
                            >
                                {item.title}
                            </button>
                            <button
                                className="bg-transparent border-0 cursor-pointer h-7 w-5 text-neutral-300 px-2 transition-colors duration-200 hover:text-red-600 active:text-red-600"
                                onClick={(e) =>
                                    toggleContextMenu(`history-${index}`, e)
                                }
                                aria-label="More options"
                                type="button"
                            >
                                <i className="fas fa-ellipsis-v text-lg"/>
                            </button>
                        </div>
                        {contextMenuOpen === `history-${index}` &&
                            menuPosition && (
                                <ContextMenuPortal
                                    x={menuPosition.x}
                                    y={menuPosition.y}
                                >
                                    <div
                                        ref={menuRef}
                                        className="bg-neutral-600 border border-neutral-400 shadow-medium w-20 text-left rounded-large overflow-hidden"
                                    >
                                        <button
                                            className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                            onClick={handleMenuAction(() =>
                                                selectChat(item.id),
                                            )}
                                        >
                                            Select
                                        </button>
                                        {/* TODO : Add UX for export status */}
                                        <button
                                            className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                            onClick={handleMenuAction(() => {
                                                setTargetChatId(item.id);
                                                setTargetChatTitle(item.title);
                                                setRenameOpen(true);
                                            })}
                                        >
                                            Rename
                                        </button>
                                        <button
                                            className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                            onClick={handleMenuAction(() =>
                                                {
                                                    setExportChatId(item.id);
                                                    setIsChatManagementOpen(true);
                                                    setModalOpen(true);
                                                },
                                            )}
                                        >
                                            Export
                                        </button>
                                        <button
                                            className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                            onClick={handleMenuAction(() =>
                                                removeChat(item.id),
                                            )}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </ContextMenuPortal>
                            )}
                    </div>
                ))}
            </div>
        </div>
    );
}
